import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  Snackbar,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  VpnKey as VpnKeyIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { User, getUsers, createUser, updateUser, deleteUser, resetUserPassword } from '../services/api';
import UserDialog from '../components/UserDialog';
import PasswordResetDialog from '../components/PasswordResetDialog';

const roleLabels: Record<string, string> = {
  'ADMIN': 'Admin',
  'BODEGA': 'Bodega',
  'CUENTAS_POR_COBRAR': 'Cobros',
  'VENTAS': 'Ventas',
  'SERVICIO_TECNICO_ADMIN': 'Serv. Admin',
  'TECNICO': 'Técnico',
  'LECTOR': 'Lector',
  'IDG': 'IDG',
  'CUSTOMER': 'Cliente',
  'CLIENT': 'Cliente'
};

const getRoleColor = (role: string): "primary" | "secondary" | "success" | "warning" | "info" | "error" => {
  switch (role) {
    case 'ADMIN': return 'primary';
    case 'BODEGA': return 'warning';
    case 'CUENTAS_POR_COBRAR': return 'success';
    case 'VENTAS': return 'info';
    case 'SERVICIO_TECNICO_ADMIN': return 'info';
    case 'TECNICO': return 'secondary';
    case 'LECTOR': return 'secondary';
    case 'IDG': return 'error';
    default: return 'secondary';
  }
};

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [passwordResetOpen, setPasswordResetOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [searchTerm, setSearchTerm] = useState('');
  const { user: currentUser } = useAuth();

  // Filter users by email or name based on search term
  const filteredUsers = users.filter(user =>
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.full_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await getUsers();
      setUsers(data);
    } catch (error) {
      setSnackbar({ open: true, message: 'Error al cargar usuarios', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleCreate = () => {
    setSelectedUser(null);
    setUserDialogOpen(true);
  };

  const handleEdit = (user: User) => {
    setSelectedUser(user);
    setUserDialogOpen(true);
  };

  const handleDeleteClick = (user: User) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const handlePasswordReset = (user: User) => {
    setSelectedUser(user);
    setPasswordResetOpen(true);
  };

  const handleSaveUser = async (userData: any) => {
    try {
      if (selectedUser) {
        await updateUser(selectedUser.id, userData);
        setSnackbar({ open: true, message: 'Usuario actualizado exitosamente', severity: 'success' });
      } else {
        await createUser(userData);
        setSnackbar({ open: true, message: 'Usuario creado exitosamente', severity: 'success' });
      }
      loadUsers();
      setUserDialogOpen(false);
    } catch (error: any) {
      throw error;
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedUser) return;

    try {
      await deleteUser(selectedUser.id);
      setSnackbar({ open: true, message: 'Usuario eliminado exitosamente', severity: 'success' });
      loadUsers();
      setDeleteDialogOpen(false);
    } catch (error: any) {
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.detail || 'Error al eliminar usuario', 
        severity: 'error' 
      });
    }
  };

  const handleResetPassword = async (userId: number, newPassword: string) => {
    try {
      await resetUserPassword(userId, newPassword);
      setSnackbar({ open: true, message: 'Contraseña reseteada exitosamente', severity: 'success' });
      setPasswordResetOpen(false);
    } catch (error: any) {
      throw error;
    }
  };

  return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Usuarios
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
          >
            Nuevo Usuario
          </Button>
        </Box>

        <Paper>
          <Box sx={{ p: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar por email o nombre..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Rol</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>ID Cliente</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Cargando...
                  </TableCell>
                </TableRow>
              ) : filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    {searchTerm ? 'No se encontraron usuarios' : 'No hay usuarios'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.full_name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {user.roles && user.roles.length > 0 ? (
                          user.roles.map((role) => (
                            <Chip
                              key={role}
                              label={roleLabels[role] || role}
                              color={getRoleColor(role)}
                              size="small"
                            />
                          ))
                        ) : (
                          <Chip label="Sin roles" size="small" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={user.is_active ? 'Activo' : 'Inactivo'} 
                        color={user.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{user.customer_id || '-'}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(user)}
                        title="Editar"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handlePasswordReset(user)}
                        title="Resetear Contraseña"
                      >
                        <VpnKeyIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(user)}
                        title="Eliminar"
                        disabled={user.id === currentUser?.id}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <UserDialog
        open={userDialogOpen}
        user={selectedUser}
        onClose={() => setUserDialogOpen(false)}
        onSave={handleSaveUser}
      />

      <PasswordResetDialog
        open={passwordResetOpen}
        user={selectedUser}
        onClose={() => setPasswordResetOpen(false)}
        onReset={handleResetPassword}
      />

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Estás seguro de que deseas eliminar al usuario <strong>{selectedUser?.full_name}</strong>?
            Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      </Container>
  );
};

export default Users;
